import { useState, useEffect, useCallback } from "react";
import { useConfig } from "../../utensils/ConfigContext";

// URL base de Baserow
const BASE_URL = "https://api.baserow.io/api/database/rows/table/239437/";

const useUsers = () => {
  const { apiToken } = useConfig();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState(null);

  // 🔄 Obtener todos los usuarios con paginación y debug
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    const allResults = [];
    const pageSize = 200;
    let page = 1;
    let keepFetching = true;

    try {
      while (keepFetching) {
        const url = `${BASE_URL}?user_field_names=true&size=${pageSize}&page=${page}`;
        console.log(`🔄 Consultando página ${page}: ${url}`);

        const response = await fetch(url, {
          headers: {
            Authorization: `Token ${apiToken}`,
          },
        });

        if (!response.ok) {
          throw new Error("Error al obtener usuarios");
        }

        const data = await response.json();

        console.log(`📦 Página ${page} resultados:`, data.results); // 🔍 Mostrar estructura real

        if (Array.isArray(data.results) && data.results.length > 0) {
          allResults.push(...data.results);
          page++;
        } else {
          keepFetching = false;
        }
      }

      setUsers(allResults);
    } catch (err) {
      console.error("❌", err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiToken]);

  // 🔎 Obtener un usuario específico por ID
  const fetchUserById = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}${id}/?user_field_names=true`, {
        headers: {
          Authorization: `Token ${apiToken}`,
        },
      });

      if (!res.ok) throw new Error("Error al obtener usuario");
      const data = await res.json();
      setSelectedUser(data);
    } catch (err) {
      console.error("❌", err.message);
    }
  };

  // ➕ Crear usuario
  const createUser = async (userData) => {
    try {
      const res = await fetch(`${BASE_URL}?user_field_names=true`, {
        method: "POST",
        headers: {
          Authorization: `Token ${apiToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      if (!res.ok) throw new Error("Error al crear usuario");
      const newUser = await res.json();
      setUsers((prev) => [...prev, newUser]);
      return newUser;
    } catch (err) {
      console.error("❌", err.message);
      return null;
    }
  };

  // ✏️ Actualizar usuario
  const updateUser = async (id, updatedData) => {
    try {
      const res = await fetch(`${BASE_URL}${id}/?user_field_names=true`, {
        method: "PATCH",
        headers: {
          Authorization: `Token ${apiToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedData),
      });

      if (!res.ok) throw new Error("Error al actualizar usuario");
      const updatedUser = await res.json();
      setUsers((prev) => prev.map((u) => (u.id === id ? updatedUser : u)));
      return updatedUser;
    } catch (err) {
      console.error("❌", err.message);
      return null;
    }
  };

  // 🗑️ Eliminar usuario
  const deleteUser = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}${id}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Token ${apiToken}`,
        },
      });

      if (!res.ok) throw new Error("Error al eliminar usuario");
      setUsers((prev) => prev.filter((u) => u.id !== id));
      return true;
    } catch (err) {
      console.error("❌", err.message);
      return false;
    }
  };

  // 🔁 Cargar usuarios al montar
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    selectedUser,
    fetchUsers,
    fetchUserById,
    createUser,
    updateUser,
    deleteUser,
  };
};

export default useUsers;
